import { isEmpty } from '@vy/array';
import type { DynamicLineItem } from '@vy/dynamic-invoice-builder';
import { isUsageDynamicLineItem } from '@vy/dynamic-invoice-builder';
import { defined } from '@vy/null';
import { useMemo } from 'react';

export const useSortedDynamicLineItems = (
  dynamicLineItems: DynamicLineItem[] | undefined,
): DynamicLineItem[] => {
  return useMemo(() => {
    if (!dynamicLineItems || isEmpty(dynamicLineItems)) {
      return [];
    }

    return sortLineItems(dynamicLineItems);
  }, [dynamicLineItems]);
};

const hasOverage = (item: DynamicLineItem): boolean => {
  if (!isUsageDynamicLineItem(item)) {
    return false;
  }

  const commitment = item.commitment;
  if (!commitment) {
    return false;
  }

  return commitment.consumption > commitment.amount;
};

/**
 * Sorts dynamic line items by:
 * 1. Items with overage come first
 * 2. Items with commitment (no overage) come second
 * 3. Other items come last
 */
const sortLineItems = (items: DynamicLineItem[]): DynamicLineItem[] => {
  return [...items].sort((a, b) => {
    const aCommitment = isUsageDynamicLineItem(a) ? a.commitment : null;
    const bCommitment = isUsageDynamicLineItem(b) ? b.commitment : null;
    const aHasCommitment = defined(aCommitment);
    const bHasCommitment = defined(bCommitment);

    const aHasOverage = hasOverage(a);
    const bHasOverage = hasOverage(b);

    if (aHasOverage && bHasOverage) {
      return 0;
    }

    if (!aHasOverage && !bHasOverage) {
      if (aHasCommitment && !bHasCommitment) {
        return -1;
      }
      if (!aHasCommitment && bHasCommitment) {
        return 1;
      }
      return 0;
    }

    if (aHasOverage) {
      return -1;
    }
    return 1;
  });
};
